// All Office.js Excel interaction lives here. This replaces the COM-based
// ExcelService / SnippetProcessingService / BookmarkService from the VSTO add-in.
import type { WritePlan } from '../types'
import { MODE_COLORS } from './modes'

export interface WriteResult {
  address: string // e.g. "B7" (without sheet prefix)
  worksheetName: string
  worksheetId: string // stable sheet id (rename-proof)
}

/**
 * Resolve a worksheet by its stable id first (rename-proof), falling back to the display name.
 * Returns a loaded Worksheet range proxy's parent sheet — callers must `await ctx.sync()`.
 */
function resolveSheet(ctx: Excel.RequestContext, worksheetId: string | undefined, worksheetName: string): Excel.Worksheet {
  if (worksheetId) {
    const byId = ctx.workbook.worksheets.getItemOrNullObject(worksheetId)
    // getItemOrNullObject accepts an id or a name; we resolve isNullObject after sync in the caller.
    return byId
  }
  return ctx.workbook.worksheets.getItem(worksheetName)
}

const SCAN_CHUNK = 50 // cells read per pass when looking for the first empty cell
const MAX_COL = 16384 // Excel column limit (XFD)

/**
 * Write a snip plan to the worksheet. For single-value/formula modes we mimic the
 * original behaviour: start at the selected cell and advance right to the first
 * empty cell. Table mode writes a block anchored at the selection.
 */
export async function writeSnip(plan: WritePlan): Promise<WriteResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet()
    sheet.load(['name', 'id'])
    const selected = ctx.workbook.getSelectedRange()
    selected.load(['rowIndex', 'columnIndex'])
    await ctx.sync()

    const startRow = selected.rowIndex
    let startCol = selected.columnIndex

    // Advance right to the first genuinely empty cell (all modes, matching the original —
    // Table anchors its block there too). Scan in chunks (not a fixed window) so a long run of
    // filled cells can't cause us to overwrite data, and stop at the sheet's right edge.
    if (plan.kind !== 'skip') {
      let col = startCol
      let found = false
      while (!found && col < MAX_COL) {
        const width = Math.min(SCAN_CHUNK, MAX_COL - col)
        const scan = sheet.getRangeByIndexes(startRow, col, 1, width)
        scan.load('values')
        await ctx.sync()
        const row = scan.values[0]
        let i = 0
        while (i < row.length && row[i] !== '' && row[i] !== null) i++
        if (i < row.length) {
          col += i
          found = true
        } else {
          col += width
        }
      }
      startCol = col
    }

    const fill = MODE_COLORS[plan.mode]
    let target: Excel.Range

    if (plan.kind === 'value') {
      target = sheet.getRangeByIndexes(startRow, startCol, 1, 1)
      target.values = [[plan.value]]
    } else if (plan.kind === 'formula') {
      target = sheet.getRangeByIndexes(startRow, startCol, 1, 1)
      target.formulas = [[plan.formula]]
    } else if (plan.kind === 'matrix') {
      const nRows = plan.matrix.length
      const nCols = Math.max(1, ...plan.matrix.map((r) => r.length))
      const normalized = plan.matrix.map((r) => {
        const copy: (string | number)[] = [...r]
        while (copy.length < nCols) copy.push('')
        return copy
      })
      target = sheet.getRangeByIndexes(startRow, startCol, nRows, nCols)
      target.values = normalized
      applyBorders(target)
      target.format.autofitColumns()
    } else {
      // 'skip' plans are filtered out before writeSnip is called.
      throw new Error('Nothing to write')
    }

    target.format.fill.color = fill
    target.load('address')
    await ctx.sync()

    // Range.address comes back as "Sheet1!B7" — strip the sheet prefix.
    const address = target.address.includes('!')
      ? target.address.split('!')[1]
      : target.address

    return { address, worksheetName: sheet.name, worksheetId: sheet.id }
  })
}

function applyBorders(range: Excel.Range): void {
  const edges: Excel.BorderIndex[] = [
    Excel.BorderIndex.edgeTop,
    Excel.BorderIndex.edgeBottom,
    Excel.BorderIndex.edgeLeft,
    Excel.BorderIndex.edgeRight,
    Excel.BorderIndex.insideHorizontal,
    Excel.BorderIndex.insideVertical,
  ]
  for (const e of edges) {
    const b = range.format.borders.getItem(e)
    b.style = Excel.BorderLineStyle.continuous
    b.weight = Excel.BorderWeight.thin
  }
}

/** Select / scroll to a cell — used when the user clicks a snippet in the tree. */
export async function navigateToCell(worksheetName: string, address: string, worksheetId?: string): Promise<void> {
  await Excel.run(async (ctx) => {
    let sheet = resolveSheet(ctx, worksheetId, worksheetName)
    sheet.load('isNullObject')
    await ctx.sync()
    // The stored id is gone (sheet deleted/recreated); fall back to the display name.
    if ((sheet as Excel.Worksheet & { isNullObject?: boolean }).isNullObject) {
      sheet = ctx.workbook.worksheets.getItem(worksheetName)
    }
    sheet.activate()
    const range = sheet.getRange(address)
    range.select()
    await ctx.sync()
  })
}

/** Clear a previously-snipped cell/range. */
export async function clearCell(worksheetName: string, address: string, worksheetId?: string): Promise<void> {
  await Excel.run(async (ctx) => {
    let sheet = resolveSheet(ctx, worksheetId, worksheetName)
    sheet.load('isNullObject')
    await ctx.sync()
    if ((sheet as Excel.Worksheet & { isNullObject?: boolean }).isNullObject) {
      sheet = ctx.workbook.worksheets.getItem(worksheetName)
    }
    const range = sheet.getRange(address)
    range.clear(Excel.ClearApplyTo.all)
    await ctx.sync()
  })
}
